import { Link, useMatch, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import Article from "~icons/material-symbols/article";
import ArticleOutline from "~icons/material-symbols/article-outline";
import HomeFill from "~icons/material-symbols/home";
import QrCodeScanner from "~icons/material-symbols/qr-code-scanner";
import Settings from "~icons/material-symbols/settings";
import SettingsOutline from "~icons/material-symbols/settings-outline";
import StoreFront from "~icons/material-symbols/storefront";
import StoreFrontOutline from "~icons/material-symbols/storefront-outline";
import { type Styles, css } from "../../styled-system/css";
import { useFooter } from "../contexts/footer-context";
import type { TenantWithFeature } from "../libs/connect";
import { t } from "../libs/i18n";
import { scanQrCode } from "../libs/qr-scanner";

const navItemCss = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textDecoration: "none",
  color: "text.primary",
  fontSize: "12px",
  pt: "8px",
  pb: "calc(env(safe-area-inset-bottom, 16px) + 8px - env(safe-area-inset-bottom, 0px))",
  "&.active": {
    color: "text.accentPrimary",
    fontWeight: 700,
  },
});

const navIconWrapperCss = css({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  rounded: "2xl",
  w: "56px",
  h: "32px",
  color: "text.secondary",
  ".active &": {
    color: "text.accentPrimary",
    fontWeight: 700,
  },
});

const navIconStyle: Styles = {
  mx: "auto",
  width: "24px",
  height: "24px",
};

const navTextCss = css({
  mt: "4px",
});

type FooterProps = {
  tenant: TenantWithFeature;
};

export default function Footer({ tenant }: FooterProps) {
  const { disabled } = useFooter();
  const navigate = useNavigate();
  const isTransactionsActive = useMatch({
    from: "/_authed/transactions/",
    shouldThrow: false,
  });
  const isTransactionDetailActive = useMatch({
    from: "/_authed/transactions/$groupId",
    shouldThrow: false,
  });
  const isTransactionActive = isTransactionsActive || isTransactionDetailActive;
  const isStoreActive = useMatch({
    from: "/_authed/stores/",
    shouldThrow: false,
  });
  const isSettingsActive = useMatch({
    from: "/_authed/settings/",
    shouldThrow: false,
  });

  const scan = useCallback(async () => {
    await scanQrCode({
      filterServiceIds: [tenant.serviceId],
      navigateFn: navigate,
    });
  }, [navigate, tenant.serviceId]);

  if (disabled) {
    return null;
  }

  return (
    <footer
      className={css({
        backgroundColor: "#FFF",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        pb: "env(safe-area-inset-bottom, 0px)",
        boxShadow: "0 -2px 10px rgba(0, 0, 0, 0.1)",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
      })}
    >
      <Link replace to="/" className={navItemCss}>
        <div className={navIconWrapperCss}>
          <HomeFill className={css(navIconStyle)} />
        </div>
        <div className={navTextCss}>{t("ホーム")}</div>
      </Link>
      <button type="button" className={navItemCss} onClick={scan}>
        <div className={navIconWrapperCss}>
          <QrCodeScanner className={css(navIconStyle)} />
        </div>
        <div className={navTextCss}>{t("読み取り")}</div>
      </button>
      <Link replace to="/transactions" className={navItemCss}>
        <div className={navIconWrapperCss}>
          {isTransactionActive ? (
            <Article className={css(navIconStyle)} />
          ) : (
            <ArticleOutline className={css(navIconStyle)} />
          )}
        </div>
        <div className={navTextCss}>{t("取引履歴")}</div>
      </Link>
      {tenant.ui.footer.showStoreInfo && (
        <Link replace to="/stores" className={navItemCss}>
          <div className={navIconWrapperCss}>
            {isStoreActive ? (
              <StoreFront className={css(navIconStyle)} />
            ) : (
              <StoreFrontOutline className={css(navIconStyle)} />
            )}
          </div>
          <div className={navTextCss}>{t("店舗情報")}</div>
        </Link>
      )}
      {tenant.ui.footer.showSettings && (
        <Link replace to="/settings" className={navItemCss}>
          <div className={navIconWrapperCss}>
            {isSettingsActive ? (
              <Settings className={css(navIconStyle)} />
            ) : (
              <SettingsOutline className={css(navIconStyle)} />
            )}
          </div>
          <div className={navTextCss}>{t("設定")}</div>
        </Link>
      )}
    </footer>
  );
}
